<template>
  <v-container class="px-4 py-8" fluid style="max-width: 1400px;">
    <!-- Admin Tickets Section -->
    <div v-if="getProfile && getProfile.role === 1" class="mb-12">
      <!-- Header -->
      <div class="mb-8 text-center text-md-left">
        <h1 class="text-h3 font-weight-bold primary--text mb-2">
          <v-icon left size="40">mdi-ticket-multiple</v-icon>
          {{ $t('sistema_tickets', 'Sistema de Tickets') }}
        </h1>
        <p class="text-subtitle-1 grey--text text--darken-1">
          Gestiona los tickets de soporte del fraccionamiento
        </p>
      </div>

      <!-- Create Ticket Dialog -->
      <v-dialog v-model="dialogCreateTicket" persistent scrollable max-width="600px">
        <v-card class="rounded-xl">
          <v-toolbar dark color="primary" flat>
            <v-toolbar-title class="font-weight-medium">
              <v-icon left>mdi-ticket-plus</v-icon>
              {{ $t('crear_ticket', 'Crear Nuevo Ticket') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogCreateTicket = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-6">
            <v-form ref="formCreateTicket" @submit.prevent="submitCreateTicket()">
              <v-text-field
                v-model="newTicket.title"
                :label="$t('titulo', 'Título')"
                prepend-inner-icon="mdi-pencil"
                :error-messages="errores.title"
                outlined rounded dense class="mb-4"
                required
              ></v-text-field>

              <v-textarea
                v-model="newTicket.description"
                :label="$t('descripcion', 'Descripción')"
                prepend-icon="mdi-text"
                :error-messages="errores.description"
                outlined rounded dense class="mb-4"
                rows="4"
              ></v-textarea>

              <v-select
                v-model="newTicket.area_id"
                :items="areas"
                item-text="name"
                item-value="id"
                :label="$t('area', 'Área')"
                prepend-inner-icon="mdi-folder-multiple"
                :error-messages="errores.area_id"
                outlined rounded dense class="mb-4"
              ></v-select>

              <v-text-field
                v-model="newTicket.client_name"
                :label="$t('nombre_cliente', 'Nombre del Cliente')"
                prepend-inner-icon="mdi-account"
                :error-messages="errores.client_name"
                outlined rounded dense class="mb-4"
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_email"
                type="email"
                :label="$t('email', 'Correo Electrónico')"
                prepend-inner-icon="mdi-email"
                :error-messages="errores.client_email"
                outlined rounded dense class="mb-4"
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_phone"
                :label="$t('telefono', 'Teléfono')"
                prepend-inner-icon="mdi-phone"
                :error-messages="errores.client_phone"
                outlined rounded dense class="mb-6"
              ></v-text-field>

              <v-alert v-if="errorAlert" type="error" outlined text class="mb-4">
                {{ errorAlert }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn color="grey" @click="dialogCreateTicket = false" rounded>
              {{ $t('cancelar', 'Cancelar') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="submitCreateTicket()"
              :loading="loadingCreateTicket"
              rounded large elevation="2"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('crear', 'Crear') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Ticket Details Dialog -->
      <v-dialog v-model="dialogTicketDetails" persistent scrollable max-width="600px">
        <v-card v-if="selectedTicket" class="rounded-xl">
          <v-toolbar dark color="primary" flat>
            <v-toolbar-title class="font-weight-medium">
              <v-icon left>mdi-information</v-icon>
              {{ $t('detalles_ticket', 'Detalles del Ticket') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogTicketDetails = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12">
                <div class="mb-4">
                  <small class="grey--text text--darken-2">{{ $t('id', 'ID') }}</small>
                  <p class="text-h6 font-weight-bold">#{{ selectedTicket.id }}</p>
                </div>
                <v-divider class="my-4"></v-divider>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('titulo', 'Título') }}</small>
                <p class="text-body1">{{ selectedTicket.title }}</p>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('descripcion', 'Descripción') }}</small>
                <p class="text-body1">{{ selectedTicket.description || '—' }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('estado', 'Estado') }}</small>
                <v-chip :color="getStatusColor(selectedTicket.status)" text-color="white" small class="mt-2">
                  {{ selectedTicket.status }}
                </v-chip>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('area', 'Área') }}</small>
                <p class="text-body1 mt-2">{{ selectedTicket.area }}</p>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('cliente', 'Cliente') }}</small>
                <p class="text-body1">{{ selectedTicket.client_name }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('email', 'Correo') }}</small>
                <p class="text-body1">{{ selectedTicket.client_email || '—' }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('fecha_creacion', 'Fecha Creación') }}</small>
                <p class="text-body1">{{ formatDate(selectedTicket.created_date) }}</p>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="dialogTicketDetails = false" rounded>
              {{ $t('cerrar', 'Cerrar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Main Content -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="elevation-4 rounded-xl">
            <v-toolbar dark color="primary" flat dense>
              <v-toolbar-title class="font-weight-medium">
                {{ $t('lista_tickets', 'Lista de Tickets') }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                color="white"
                text
                small
                @click="loadTickets()"
                :loading="loadingTickets"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <v-btn
                color="primary"
                @click="openCreateDialog()"
                rounded large elevation="2" class="mb-6"
              >
                <v-icon left>mdi-plus</v-icon>
                {{ $t('nuevo_ticket', 'Nuevo Ticket') }}
              </v-btn>

              <v-data-table
                :headers="headers"
                :items="tickets"
                :loading="loadingTickets"
                :footer-props="{
                  itemsPerPageOptions: [5, 10, 25, 50],
                  itemsPerPageText: $t('filas_por_pagina', 'Filas por página')
                }"
                class="elevation-1 rounded-lg"
                @click:row="openDetailsDialog"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" text-color="white" small>
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.created_date="{ item }">
                  {{ formatDate(item.created_date) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-icon small @click.stop="openDetailsDialog(item)" class="mr-2">
                    mdi-eye
                  </v-icon>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-8">
                    <v-icon large color="grey" class="mb-2">mdi-inbox-multiple</v-icon>
                    <p class="grey--text">{{ $t('no_hay_tickets', 'No hay tickets disponibles') }}</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Statistics Cards -->
      <v-row v-if="tickets.length > 0">
        <v-col cols="12" sm="6" md="3">
          <v-card class="elevation-4 rounded-xl text-center py-6">
            <div class="primary pa-2 rounded-lg d-inline-block mb-4">
              <v-icon large color="white">mdi-ticket</v-icon>
            </div>
            <p class="text-h5 font-weight-bold primary--text">{{ tickets.length }}</p>
            <p class="grey--text text--darken-1 text-caption">{{ $t('total_tickets', 'Total de Tickets') }}</p>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="elevation-4 rounded-xl text-center py-6">
            <div class="primary pa-2 rounded-lg d-inline-block mb-4">
              <v-icon large color="white">mdi-alert-circle</v-icon>
            </div>
            <p class="text-h5 font-weight-bold" style="color: #FF9800;">{{ countByStatus('Nuevo') }}</p>
            <p class="grey--text text--darken-1 text-caption">{{ $t('nuevos', 'Nuevos') }}</p>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="elevation-4 rounded-xl text-center py-6">
            <div class="primary pa-2 rounded-lg d-inline-block mb-4">
              <v-icon large color="white">mdi-progress-clock</v-icon>
            </div>
            <p class="text-h5 font-weight-bold" style="color: #2196F3;">{{ countByStatus('En progreso') }}</p>
            <p class="grey--text text--darken-1 text-caption">{{ $t('en_progreso', 'En Progreso') }}</p>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="elevation-4 rounded-xl text-center py-6">
            <div class="primary pa-2 rounded-lg d-inline-block mb-4">
              <v-icon large color="white">mdi-check-circle</v-icon>
            </div>
            <p class="text-h5 font-weight-bold" style="color: #4CAF50;">{{ countByStatus('Resuelto') }}</p>
            <p class="grey--text text--darken-1 text-caption">{{ $t('resueltos', 'Resueltos') }}</p>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Non-Admin Message -->
    <div v-else class="text-center py-12">
      <v-icon size="80" color="grey" class="mb-4">mdi-lock-outline</v-icon>
      <h2 class="text-h5 grey--text text--darken-1 mb-2">{{ $t('acceso_no_autorizado', 'Acceso no Autorizado') }}</h2>
      <p class="text-body2 grey--text">{{ $t('solo_administradores', 'Solo los administradores pueden acceder a esta sección') }}</p>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { ticketsService } from '@/services/ticketsService'

export default {
  name: 'Ayuda',
  data () {
    return {
      tickets: [],
      areas: [],
      selectedTicket: null,
      dialogCreateTicket: false,
      dialogTicketDetails: false,
      loadingTickets: false,
      loadingCreateTicket: false,
      loadingAreas: false,
      errorAlert: '',
      errores: {},
      newTicket: {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      },
      headers: []
    }
  },
  computed: {
    ...mapGetters(['getProfile'])
  },
  beforeMount () {
    this.headers = [
      {
        text: this.$t('id'),
        align: 'left',
        sortable: true,
        value: 'id'
      },
      {
        text: this.$t('titulo'),
        align: 'left',
        sortable: true,
        value: 'title'
      },
      {
        text: this.$t('cliente'),
        align: 'left',
        sortable: true,
        value: 'client_name'
      },
      {
        text: this.$t('area'),
        align: 'left',
        sortable: true,
        value: 'area'
      },
      {
        text: this.$t('estado'),
        align: 'center',
        sortable: true,
        value: 'status'
      },
      {
        text: this.$t('fecha_creacion'),
        align: 'left',
        sortable: true,
        value: 'created_date'
      },
      {
        text: this.$t('acciones'),
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ]
  },
  created () {
    if (this.getProfile && this.getProfile.role === 1) {
      this.loadAreas()
      this.loadTickets()
    }
  },
  methods: {
    async loadAreas () {
      this.loadingAreas = true
      try {
        const response = await ticketsService.getAreas()
        if (response.success) {
          this.areas = response.data
        }
      } catch (error) {
        console.error('Error loading areas:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar las áreas')
      } finally {
        this.loadingAreas = false
      }
    },

    async loadTickets () {
      this.loadingTickets = true
      try {
        const response = await ticketsService.getAllTickets(100)
        if (response.success) {
          this.tickets = response.data
        }
      } catch (error) {
        console.error('Error loading tickets:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar los tickets')
      } finally {
        this.loadingTickets = false
      }
    },

    openCreateDialog () {
      this.dialogCreateTicket = true
      this.resetForm()
      this.errorAlert = ''
    },

    openDetailsDialog (ticket) {
      this.selectedTicket = ticket
      this.dialogTicketDetails = true
    },

    async submitCreateTicket () {
      this.errores = {}
      this.errorAlert = ''

      if (!this.newTicket.title || !this.newTicket.title.trim()) {
        this.errores.title = ['El título es requerido']
        return
      }

      this.loadingCreateTicket = true
      try {
        const ticketData = {
          title: this.newTicket.title,
          description: this.newTicket.description || '',
          area_id: this.newTicket.area_id || null,
          client_name: this.newTicket.client_name || '',
          client_email: this.newTicket.client_email || '',
          client_phone: this.newTicket.client_phone || ''
        }

        const response = await ticketsService.createTicket(ticketData)

        if (response.success) {
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Ticket creado exitosamente')
          this.$store.commit('snackbar_change_color', 'success')
          this.dialogCreateTicket = false
          this.resetForm()
          await this.loadTickets()
        }
      } catch (error) {
        console.error('Error creating ticket:', error)
        if (error.response && error.response.status === 400 && error.response.data) {
          const data = error.response.data
          if (data.error) {
            this.errorAlert = data.message || data.error
          } else {
            this.errores = data
          }
        } else {
          this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
        }
      } finally {
        this.loadingCreateTicket = false
      }
    },

    resetForm () {
      this.newTicket = {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      }
      this.errores = {}
      this.errorAlert = ''
    },

    getStatusColor (status) {
      const colorMap = {
        'Nuevo': '#FF9800',
        'En progreso': '#2196F3',
        'Resuelto': '#4CAF50',
        'Cerrado': '#9E9E9E'
      }
      return colorMap[status] || '#757575'
    },

    countByStatus (status) {
      return this.tickets.filter(ticket => ticket.status === status).length
    },

    formatDate (dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.v-data-table {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
